import { Box, Stack, styled } from '@mui/material';
import { ConversationChannelTypeExtended } from '@provision/chat-utils';

import {
  ChannelIdentifiers,
  ConversationDraftScope,
  ConversationDraftSideEffectActionType,
  ConversationParticipantEntity,
  ConversationStatus,
} from '@provision/conversation-types';
import { EntityIdentifier } from '@provision/entity-types';
import { renderRichTextDocumentToHtml, stableStringify } from '@provision/formatting-utils';
import { useApolloClient, useMutation } from '@provision/graphql-client';
import {
  Content,
  Editor,
  getAttachmentsFromEditor,
  isConversationAttachmentWithUrl,
  isFile,
  JSONContent,
  MentionsPopupState,
  RichTextEditorFile,
} from '@provision/rich-text-editor';
import dayjs from 'dayjs';
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Subject } from 'rxjs';
import { DynamicIcon, ShortcutText } from 'src/components';
import { MentionPopup } from 'src/components/MentionPopup/mention-popup';
import MessageTypeSelector, {
  MessageTypeSelectorProps,
} from 'src/components/MessageTypeSelector/message-type-selector';
import { useRegisterQuickActions } from 'src/components/QuickActions/use-register-quick-actions';
import RichTextEditor from 'src/components/RichTextEditor/rich-text-editor';
import { appShortcuts } from 'src/constants/appShortcuts';
import {
  Conversation,
  ConversationParticipantItem,
  DraftAttachmentInput,
  isDraftNonEmpty,
  Macro,
  MacroActionRule,
  PreparedConversationParticipantOption,
  useConversationMessageDraft,
  useCurrentUserProfile,
  useGroupRules,
  UserGroupRules,
} from 'src/graphql';
import { NO_SUBJECT_PLACEHOLDER } from 'src/graphql/entities/conversation/conversation.mappings';
import { SubmitConversationDataParticipant } from 'src/graphql/entities/conversation/conversation.types';
import {
  DELETE_CONVERSATION_MESSAGE_DRAFT,
  GET_CONVERSATION_MESSAGE_DRAFT_QUERY,
} from 'src/graphql/entities/conversation-message-draft/conversation-message-draft.operations';
import type { GetUserConversationMessageDraft } from 'src/graphql/entities/conversation-message-draft/hooks/use-conversation-message-draft';
import type { DeleteConversationMessageDraft } from 'src/graphql/entities/conversation-message-draft/hooks/use-delete-conversation-message-draft';
import {
  findServerDraftIdByScope,
  isOptimisticDraftId,
} from 'src/graphql/entities/conversation-message-draft/hooks/utils';
import { useFileUpload } from 'src/graphql/entities/files-upload';

import { AiAssistantTrigger } from 'src/pages/Conversations/Chat/AiAssistant/ai-assistant-trigger';
import { PendingAiSuggestionHeader } from 'src/pages/Conversations/Chat/AiAssistant/pending-ai-suggestion-header';
import { SuggestionGenerationLoader } from 'src/pages/Conversations/Chat/AiAssistant/suggestion-generation-loader';
import { useAiAssistant } from 'src/pages/Conversations/Chat/AiAssistant/use-ai-assistant';
import {
  EditorDoc,
  readMacrosFromDoc,
} from 'src/pages/Conversations/Chat/conversation-draft-macros';
import {
  DraftSchedule,
  DraftScheduleSendAndAction,
} from 'src/pages/Conversations/Chat/conversation-draft-schedule';
import { clearLocalDraftScope } from 'src/pages/Conversations/Chat/conversation-draft-storage';
import {
  getActiveChannels,
  getPreparedParticipants,
  getSenderParticipant,
  hasEmailChannel,
  validateMacroActions,
} from 'src/pages/Conversations/Chat/InputField/utils';
import {
  ActionChipIcon,
  AppliedMacroActionsList,
  CustomChip,
} from 'src/pages/Conversations/Chat/Macros/applied-macro-actions-list';
import { MacrosTrigger } from 'src/pages/Conversations/Chat/Macros/macros-trigger';
import { ParticipationPanel } from 'src/pages/Conversations/Chat/ParticipationPanel/participation-panel';
import { SelectedParticipants } from 'src/pages/Conversations/Chat/ParticipationPanel/types';
import { UseSelectedParticipants } from 'src/pages/Conversations/Chat/ParticipationPanel/use-selected-participants';

import { privateNoteButtonStyles } from 'src/pages/Conversations/Chat/shared-styles';
import { useConversationDraft } from 'src/pages/Conversations/Chat/use-conversation-draft';
import { useDraftCommitGuards } from 'src/pages/Conversations/Chat/use-draft-commit-guards';
import { isSameParticipantOption } from 'src/pages/Conversations/Chat/utils';
import { getConversationAttachmentUrl } from 'src/pages/Conversations/SidePanel/ConversationDetails/Attachments/attachment-utils';
import { Modals, useModalMethods } from 'src/store/modals';
import { useSelectedConversationRequesterStore } from 'src/store/selected-conversation-requester';
import {
  useConfirmationModal,
  useDisplayTime,
  useNotifications,
  useRegisterShortcuts,
} from 'src/utils/hooks';
import { parseJsonSafely } from 'src/utils/objects';
import { useScreenDimensions } from 'src/utils/system';
import { htmlToPlainText } from 'src/utils/text-parsing';
import ExistingScheduledConflictModal from './existing-scheduled-conflict-modal';
import { useDraftScheduling } from './hooks/use-draft-scheduling';
import { normalizeOutgoingMessageHtml } from './normalize-outgoing-message-html';
import ScheduleSendModal from './schedule-send-modal';
import ScheduledBanner from './scheduled-banner';
import SendOptionsButton from './send-options-button';

const InputFieldBackground = styled(Stack, {
  name: 'InputFieldBackground',
  shouldForwardProp: (prop) => prop !== 'isInternalNote' && prop !== 'hasPendingAiSuggestion',
})<{
  isInternalNote: boolean;
  hasPendingAiSuggestion: boolean;
}>(({ theme, isInternalNote, hasPendingAiSuggestion }) => {
  const basedStyles = {
    minHeight: 0,
    height: '100%',
    position: 'relative' as const,
    flexGrow: 1,
    backgroundColor: isInternalNote
      ? theme.palette.custom.OV.custom.InternalNotes.noteInputContainer
      : theme.palette.custom.OV.sys.surfaceBright,
    borderRadius: hasPendingAiSuggestion ? `0 0 8px 8px` : theme.shape.borderRadius,
    padding: theme.spacing(1),
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
    overflow: 'visible',
    zIndex: 1,
  };

  if (isInternalNote) {
    return {
      ...basedStyles,
      ...privateNoteButtonStyles(theme),
    };
  }

  return basedStyles;
});

const MessageTypeSelectorBackground = styled(Stack, {
  name: 'MessageTypeSelectorBackground',
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({ isMobile, theme }) => ({
  ...(isMobile && { minHeight: 0, maxHeight: '50%' }),
  paddingBottom: theme.spacing(1),
  paddingTop: theme.spacing(1),
  alignItems: 'stretch',
  flex: '0 0 min-content',
}));

interface onSubmitInput {
  message: {
    body: string;
    isPrivate: boolean;
    mentions?: EntityIdentifier[];
    conversationMessageId?: string;
  };
  macros: (MacroActionRule & { isValid: boolean })[];
  activeChannels?: ChannelIdentifiers[];
  participants: SubmitConversationDataParticipant[];
  sendAndActions?: DraftScheduleSendAndAction[];
}

interface InputFieldProps {
  onSubmit: (input: onSubmitInput) => Promise<void>;
  messageTypeOptions: MessageTypeSelectorProps['options'];
  conversation: Conversation;
  participants: ConversationParticipantItem[];
  files?: RichTextEditorFile[];
  setFiles?: Dispatch<SetStateAction<RichTextEditorFile[]>>;
  selectedParticipantsState: UseSelectedParticipants;
  fieldAutoSize?: boolean;
  fieldMinHeight?: string;
}

const editorOptions = { clearOnSubmit: true };

const CHAT_PLACEHOLDER_TEXT = `Write, type '/' for commands, '@' for mentions…`;

const placeholderOptions = {
  placeholder: CHAT_PLACEHOLDER_TEXT,
  emptyLinePlaceholderText: CHAT_PLACEHOLDER_TEXT,
};

export const InputField = ({
  onSubmit,
  messageTypeOptions,
  conversation,
  files,
  setFiles,
  selectedParticipantsState,
  fieldAutoSize = true,
  fieldMinHeight,
}: InputFieldProps) => {
  const { displayTime } = useDisplayTime();
  const { uploadFile } = useFileUpload();

  const buildAttachmentsManifest = useCallback(
    async (sourceFiles: RichTextEditorFile[]): Promise<DraftAttachmentInput[]> => {
      const newFiles = sourceFiles.filter(isFile);
      const existing = sourceFiles.filter(isConversationAttachmentWithUrl);

      const uploaded = newFiles.length
        ? await getAttachmentsFromEditor({
            files: newFiles,
            attachmentsBucketFolder: `${conversation.partnerId}/${conversation.conversationId}`,
            uploadFile,
          })
        : [];

      return [
        ...uploaded.map((file) => ({
          fileKey: file.url.split('/').at(-1)?.split('.')[0] ?? '',
          fileName: file.name,
          contentType: file.type,
          size: file.size,
        })),
        ...existing.map((attachment) => ({
          fileKey: attachment.fileKey,
          fileName: attachment.fileName,
          contentType: attachment.contentType,
          size: attachment.size,
        })),
      ];
    },
    [conversation.conversationId, conversation.partnerId, uploadFile],
  );

  const {
    getToParticipant,
    removeParticipant,
    selectedParticipants,
    setToParticipant,
    toIsModified,
    ...selectedParticipantsCallbacks
  } = selectedParticipantsState;
  const [isOffline, setIsOffline] = useState(false);

  const location = useLocation();
  const forceMessageType = (
    location.state as { forceMessageType?: ConversationChannelTypeExtended }
  )?.forceMessageType;

  const { isMobile } = useScreenDimensions();
  const editorRef = useRef<Editor | null>(null);
  const lastAppliedRef = useRef(''); // what we last pushed into the editor
  const rteSubmitButtonRef = useRef<MutableRefObject<HTMLButtonElement | null> | null>(null);
  const sendNowHandlerRef = useRef<(() => void) | null>(null);
  const pendingSendActionsRef = useRef<DraftScheduleSendAndAction[] | null>(null);
  const pendingSendDeferredRef = useRef<{
    resolve: () => void;
    reject: (error: unknown) => void;
  } | null>(null);

  const { showSuccessAlert, showErrorAlert } = useNotifications();
  const { checkValidity } = useGroupRules();

  const { drafts: userDrafts } = useConversationMessageDraft();
  const restoredReplyIsPrivate = useMemo(() => {
    const latest = userDrafts.find((draft) => draft.conversation?.id === conversation.id);
    return latest ? latest.isPrivate : null;
  }, [userDrafts, conversation.id]);

  const userChangedModeRef = useRef(false);
  const replyModeRestoredRef = useRef(false);

  const [isInternalNote, setIsInternalNote] = useState(() => {
    if (forceMessageType) {
      return forceMessageType === ConversationChannelTypeExtended.InternalNote;
    }
    if (restoredReplyIsPrivate !== null) {
      return restoredReplyIsPrivate;
    }
    return true;
  });

  useEffect(() => {
    if (forceMessageType) return;
    if (userChangedModeRef.current || replyModeRestoredRef.current) return;
    if (restoredReplyIsPrivate === null) return;
    replyModeRestoredRef.current = true;
    setIsInternalNote(restoredReplyIsPrivate);
  }, [forceMessageType, restoredReplyIsPrivate]);

  // Base draft instance
  const {
    draftContent: baseDraftContent,
    debounceUpdateDraft: debounceUpdateBaseDraft,
    flushUpdateDraft: flushUpdateBaseDraft,
    clearDraft: clearBaseDraft,
    draftSchedule: baseDraftSchedule,
    updateDraftSchedule: updateBaseDraftSchedule,
  } = useConversationDraft({
    conversationId: conversation.id,
    conversationPartnerId: conversation.partnerId,
    isPrivate: isInternalNote,
    conversation,
  });

  // Scheduled draft instance (separate)
  const {
    draftContent: scheduledDraftContent,
    debounceUpdateDraft: debounceUpdateScheduledDraft,
    flushUpdateDraft: flushUpdateScheduledDraft,
    clearDraft: clearScheduledDraft,
    draftSchedule: scheduledDraftSchedule,
    updateDraftSchedule: updateScheduledDraftSchedule,
    isDraftSaving: isScheduledDraftSaving,
    serverDraft: scheduledServerDraft,
  } = useConversationDraft({
    conversationId: conversation.id,
    conversationPartnerId: conversation.partnerId,
    isPrivate: isInternalNote,
    conversation,
    scheduled: true,
  });

  const scheduledServerAttachments = useMemo(
    () =>
      (scheduledServerDraft?.attachments ?? []).filter(
        (attachment): attachment is NonNullable<typeof attachment> =>
          Boolean(attachment?.fileKey),
      ),
    [scheduledServerDraft?.attachments],
  );
  const scheduledAttachmentFiles = useMemo<RichTextEditorFile[]>(
    () =>
      scheduledServerAttachments.map(
        (attachment) =>
          ({
            ...attachment,
            url: getConversationAttachmentUrl(attachment) ?? '',
          }) as RichTextEditorFile,
      ),
    [scheduledServerAttachments],
  );
  const [scheduledFiles, setScheduledFiles] = useState<RichTextEditorFile[]>([]);

  useEffect(() => {
    setScheduledFiles(scheduledAttachmentFiles);
  }, [scheduledAttachmentFiles]);

  const apolloClient = useApolloClient();
  const [deleteScheduledDraftMutation] = useMutation<DeleteConversationMessageDraft>(
    DELETE_CONVERSATION_MESSAGE_DRAFT,
  );

  const deleteScheduledDraftRecord = useCallback(
    async (draft: { id: string; conversationId: string; isPrivate: boolean }) => {
      const removeFromCache = (targetId: string) => {
        const { cache } = apolloClient;
        const cacheId = cache.identify({
          __typename: 'ConversationMessageDraft',
          id: targetId,
        });
        if (cacheId) {
          cache.evict({ id: cacheId });
        }
        cache.updateQuery<GetUserConversationMessageDraft>(
          { query: GET_CONVERSATION_MESSAGE_DRAFT_QUERY },
          (existing) =>
            existing
              ? {
                  getUserConversationMessageDraft: {
                    records: existing.getUserConversationMessageDraft.records.filter(
                      (record) => record.id !== targetId,
                    ),
                  },
                }
              : existing,
        );
        cache.gc();
      };

      let id = draft.id;

      if (isOptimisticDraftId(id)) {
        const realId = await findServerDraftIdByScope(apolloClient, {
          conversationId: draft.conversationId,
          isPrivate: draft.isPrivate,
          isScheduled: true,
        });

        if (!realId) {
          removeFromCache(id);
          clearLocalDraftScope(
            draft.conversationId,
            draft.isPrivate,
            ConversationDraftScope.Scheduled,
          );
          return;
        }
        id = realId;
      }

      await deleteScheduledDraftMutation({
        variables: {
          conditions: [{ attribute: 'id', operator: '=', value: id }],
        },
        optimisticResponse: {
          deleteConversationMessageDraft: [{ id }],
        },
        update: () => removeFromCache(id),
        context: { omitIsolationModeHeader: true },
      });
      clearLocalDraftScope(
        draft.conversationId,
        draft.isPrivate,
        ConversationDraftScope.Scheduled,
      );
    },
    [apolloClient, deleteScheduledDraftMutation],
  );

  const handleSendAndActions = useCallback(
    async (actions: DraftScheduleSendAndAction[]): Promise<void> => {
      if (!actions.length) return;
      if (!sendNowHandlerRef.current) {
        showErrorAlert('Unable to send message right now. Please try again.');
        return;
      }

      pendingSendActionsRef.current = actions;
      const promise = new Promise<void>((resolve, reject) => {
        pendingSendDeferredRef.current = { resolve, reject };
      });

      try {
        sendNowHandlerRef.current();
      } catch (error) {
        pendingSendActionsRef.current = null;
        pendingSendDeferredRef.current = null;
        throw error;
      }

      await promise;
    },
    [showErrorAlert],
  );

  const [macroActions, setMacroActions] = useState<(MacroActionRule & { isValid: boolean })[]>(
    [],
  );
  const macroActionsRef = useRef(macroActions);
  useEffect(() => {
    macroActionsRef.current = macroActions;
  }, [macroActions]);

  const syncScheduledDraftRef =
    useRef<(schedule?: DraftSchedule) => Promise<void> | void>(undefined);

  const syncScheduledDraft = useCallback(async (schedule?: DraftSchedule): Promise<void> => {
    const handler = syncScheduledDraftRef.current;
    if (handler) {
      await handler(schedule);
    }
  }, []);

  const flushActiveDraftRef = useRef<() => void | Promise<void>>(undefined);

  const flushActiveDraft = useCallback(() => flushActiveDraftRef.current?.(), []);

  const scheduledDraftContentRef = useRef(scheduledDraftContent);

  useEffect(() => {
    scheduledDraftContentRef.current = scheduledDraftContent;
  }, [scheduledDraftContent]);

  const isViewingScheduledMessageRef = useRef(false);
  const baseDraftContentRef = useRef(baseDraftContent);

  useEffect(() => {
    baseDraftContentRef.current = baseDraftContent;
  }, [baseDraftContent]);

  const convertScheduledDraftToRegular = useCallback(async () => {
    const flushActive = flushActiveDraftRef.current;
    if (flushActive) {
      await flushActive();
    }

    const storedScheduledContent = scheduledDraftContentRef.current || '';
    const parsedScheduledDoc = parseJsonSafely<EditorDoc>(storedScheduledContent);
    const snapshot = isViewingScheduledMessageRef.current
      ? buildDraftJson(editorRef.current, macroActionsRef.current, parsedScheduledDoc)
      : storedScheduledContent;

    scheduledDraftContentRef.current = snapshot;
    flushUpdateBaseDraft(snapshot);
    await updateBaseDraftSchedule({
      scheduledAt: null,
      sideEffects: null,
      sendUnless: null,
      discardedAt: null,
    });
    if (scheduledServerDraft?.id) {
      await deleteScheduledDraftRecord({
        id: scheduledServerDraft.id,
        conversationId: scheduledServerDraft.conversationId ?? conversation.id,
        isPrivate: Boolean(scheduledServerDraft.isPrivate),
      });
    } else {
      clearLocalDraftScope(
        conversation.id,
        scheduledServerDraft?.isPrivate ?? isInternalNote,
        ConversationDraftScope.Scheduled,
      );
    }
    scheduledDraftContentRef.current = '';
    lastAppliedRef.current = snapshot;
  }, [
    conversation.id,
    deleteScheduledDraftRecord,
    flushUpdateBaseDraft,
    isInternalNote,
    scheduledServerDraft,
    updateBaseDraftSchedule,
  ]);

  const { confirm } = useConfirmationModal();

  // Unschedule while keeping the current input (do not override base content)
  const unscheduleDraftKeepCurrent = useCallback(async () => {
    const flushActive = flushActiveDraftRef.current;
    if (flushActive) {
      await flushActive();
    }

    // Clear any schedule on the base draft
    await updateBaseDraftSchedule({
      scheduledAt: null,
      sideEffects: null,
      sendUnless: null,
      discardedAt: null,
    });

    // Remove the scheduled draft record/scope
    if (scheduledServerDraft?.id) {
      await deleteScheduledDraftRecord({
        id: scheduledServerDraft.id,
        conversationId: scheduledServerDraft.conversationId ?? conversation.id,
        isPrivate: Boolean(scheduledServerDraft.isPrivate),
      });
    } else {
      clearLocalDraftScope(
        conversation.id,
        scheduledServerDraft?.isPrivate ?? isInternalNote,
        ConversationDraftScope.Scheduled,
      );
    }

    scheduledDraftContentRef.current = '';
    // Keep editor content as-is; do not change lastAppliedRef
  }, [
    conversation.id,
    deleteScheduledDraftRecord,
    isInternalNote,
    scheduledServerDraft,
    updateBaseDraftSchedule,
  ]);

  const computeIsMessageEmpty = useCallback((): boolean => {
    const hasText = editorHasContent(editorRef.current);
    const hasFiles = (files?.length ?? 0) > 0;
    return !(hasText || hasFiles);
  }, [files?.length]);

  // Wrapper that asks whether to keep current input or scheduled draft content
  const unscheduleDraftWithConfirm = useCallback(async () => {
    // If currently viewing scheduled message, check base draft content instead
    const messageIsEmpty = isViewingScheduledMessageRef.current
      ? !(baseDraftContentRef.current && baseDraftContentRef.current.trim().length > 0)
      : computeIsMessageEmpty();
    if (messageIsEmpty) {
      await convertScheduledDraftToRegular();
      return;
    }

    const confirmed = await confirm({
      title: 'Cancel schedule',
      body: 'You have text in the input. If you confirm, the scheduled message will be lost.',
      variant: 'delete',
    });

    if (confirmed) {
      await unscheduleDraftKeepCurrent();
    }
  }, [
    computeIsMessageEmpty,
    confirm,
    convertScheduledDraftToRegular,
    unscheduleDraftKeepCurrent,
  ]);

  const {
    hasScheduledMessage,
    scheduledLabel,
    scheduledDiscardedLabel,
    isScheduleDiscarded,
    sendOptions,
    isScheduleModalOpen,
    openScheduleModal,
    closeScheduleModal,
    handleSendOptionSelect,
    handleScheduleModalSubmit,
    handleDiscardScheduledMessage,
    handleViewScheduledMessage,
    handleDoneViewingScheduledMessage,
    handleReschedule,
    isViewingScheduledMessage,
    isApplyingSendOption,
    scheduledConditions,
  } = useDraftScheduling({
    scheduledDraftSchedule,
    displayTime,
    // @ts-expect-error temp
    flushDraft: flushActiveDraft,
    updateDraftSchedule: updateScheduledDraftSchedule,
    executeSendAndActions: handleSendAndActions,
    syncScheduledDraft,
    unscheduleDraft: unscheduleDraftWithConfirm,
  });

  useRegisterQuickActions(
    useMemo(
      () => [
        {
          id: 'conversation-open-send-options',
          title: 'Open send options',
          description: 'Schedule or apply send-and actions',
          icon: <DynamicIcon name="schedule" />,
          group: 'Conversation Actions',
          handler: openScheduleModal,
          order: 180,
          keywords: ['schedule', 'send', 'snooze'],
        },
      ],
      [openScheduleModal],
    ),
  );

  useEffect(() => {
    isViewingScheduledMessageRef.current = isViewingScheduledMessage;
  }, [isViewingScheduledMessage]);

  const activeRawDraft = useMemo(
    () => (isViewingScheduledMessage ? scheduledDraftContent : baseDraftContent) || '',
    [isViewingScheduledMessage, scheduledDraftContent, baseDraftContent],
  );

  const hasSendableScheduledMessage =
    hasScheduledMessage && isDraftNonEmpty(scheduledDraftContent);

  const parsedDraft = useMemo(
    () => parseJsonSafely<EditorDoc>(activeRawDraft),
    [activeRawDraft],
  );

  useEffect(() => {
    syncScheduledDraftRef.current = async (schedule?: DraftSchedule) => {
      const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
      const attachments = await buildAttachmentsManifest(files ?? []);
      flushUpdateScheduledDraft(snapshot, schedule, attachments);

      await clearBaseDraft();

      editorRef.current?.commands?.clearContent?.(true);
      lastAppliedRef.current = '';
      setMacroActions([]);
      setFiles?.([]);
    };
  }, [
    buildAttachmentsManifest,
    files,
    clearBaseDraft,
    flushUpdateScheduledDraft,
    macroActions,
    parsedDraft,
    setFiles,
    setMacroActions,
  ]);

  useEffect(() => {
    flushActiveDraftRef.current = () => {
      const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
      lastAppliedRef.current = snapshot;
      if (isViewingScheduledMessage) {
        flushUpdateScheduledDraft(snapshot);
        return;
      }
      flushUpdateBaseDraft(snapshot);
    };
  }, [
    flushUpdateBaseDraft,
    flushUpdateScheduledDraft,
    isViewingScheduledMessage,
    macroActions,
    parsedDraft,
  ]);

  const notifySendAndActions = useCallback(
    (actions: DraftScheduleSendAndAction[]): void => {
      if (!actions.length) return;

      const fragments: string[] = [];

      actions.forEach((action) => {
        if (action.type === ConversationDraftSideEffectActionType.Status) {
          const resolved = resolveStatusAction(action.value);
          if (resolved) {
            fragments.push(resolved.label);
          }
        }

        if (action.type === ConversationDraftSideEffectActionType.Snooze && action.value) {
          const snoozeAt = dayjs(action.value);
          if (snoozeAt.isValid()) {
            const datePart = snoozeAt.format('MMM D');
            fragments.push(
              `conversation snoozed until ${datePart} at ${displayTime(snoozeAt)}`,
            );
          } else {
            fragments.push('conversation snoozed');
          }
        }
      });

      if (!fragments.length) {
        return;
      }

      const message = fragments.join(' and ');
      showSuccessAlert(`Message sent and ${message}`);
    },
    [displayTime, showSuccessAlert],
  );

  useEffect(() => {
    if (parsedDraft) setMacroActions(readMacrosFromDoc(parsedDraft));
    else setMacroActions([]);
  }, [parsedDraft]);

  // Hydrate editor from active draft only
  useEffect(() => {
    if (!editorRef.current) return;
    if (!activeRawDraft) {
      editorRef.current.commands?.clearContent?.(true);
      lastAppliedRef.current = '';
      return;
    }
    applyEditorContent(editorRef.current, activeRawDraft, lastAppliedRef);
  }, [activeRawDraft]);

  // Persist json on blur to the active draft
  const handleEditorBlur = useCallback(
    (_: React.FocusEvent, editor: Editor | null) => {
      if (!editor) return;
      const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
      if (snapshot === lastAppliedRef.current) return;
      lastAppliedRef.current = snapshot;

      if (isViewingScheduledMessage) {
        flushUpdateScheduledDraft(snapshot);
      } else {
        flushUpdateBaseDraft(snapshot);
      }
    },
    [
      isViewingScheduledMessage,
      macroActions,
      parsedDraft,
      flushUpdateBaseDraft,
      flushUpdateScheduledDraft,
    ],
  );

  // persist attachment add/remove made while viewing the scheduled message.
  useEffect(() => {
    if (!isViewingScheduledMessage) return undefined;

    const attachmentsKey = (list: RichTextEditorFile[]): string =>
      list
        .map((file) => (isFile(file) ? file.name : file.fileKey))
        .sort()
        .join('|');

    if (attachmentsKey(scheduledFiles) === attachmentsKey(scheduledAttachmentFiles)) {
      return undefined;
    }

    const timer = setTimeout(() => {
      const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
      void buildAttachmentsManifest(scheduledFiles).then((attachments) =>
        flushUpdateScheduledDraft(snapshot, undefined, attachments),
      );
    }, 600);

    return () => clearTimeout(timer);
  }, [
    isViewingScheduledMessage,
    scheduledFiles,
    scheduledAttachmentFiles,
    buildAttachmentsManifest,
    flushUpdateScheduledDraft,
    macroActions,
    parsedDraft,
  ]);

  // AI suggestion writes to the active draft
  const setAiSuggestion = useCallback(
    (input: string) => {
      applyEditorContent(editorRef.current, input, lastAppliedRef);
      const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
      lastAppliedRef.current = snapshot;
      if (isViewingScheduledMessage) {
        flushUpdateScheduledDraft(snapshot);
      } else {
        flushUpdateBaseDraft(snapshot);
      }
    },
    [
      isViewingScheduledMessage,
      macroActions,
      parsedDraft,
      flushUpdateBaseDraft,
      flushUpdateScheduledDraft,
    ],
  );

  const {
    pendingAiSuggestion,
    selectedPromptName,
    isGeneratingAiSuggestion,
    handleRefineSuggestion,
    handleAcceptAiSuggestion,
    handleDismissAiSuggestion,
    resetAiAssistant,
    getInitialSuggestion,
    conversationMessageId,
  } = useAiAssistant({
    setContent: editorRef.current ? setAiSuggestion : undefined,
    conversationId: conversation.id,
    conversationPartnerId: conversation.partnerId,
  });

  const { selectedConversationRequester, setPartnerSelectedConversationRequester } =
    useSelectedConversationRequesterStore((state) => state);
  const currentRequesterId = useRef(selectedConversationRequester?.id);

  useEffect(() => {
    if (currentRequesterId.current === selectedConversationRequester?.id) return;
    currentRequesterId.current = selectedConversationRequester?.id;

    if (!selectedParticipants.to && !selectedConversationRequester) return;

    if (!selectedConversationRequester) {
      if (!toIsModified) {
        removeParticipant(getToParticipant()!);
      }
      return;
    }

    if (
      !selectedConversationRequester.email ||
      selectedConversationRequester.participantEntity === ConversationParticipantEntity.Rsm
    ) {
      return;
    }

    if (
      !selectedParticipants.to ||
      !isSameParticipantOption(selectedParticipants.to, selectedConversationRequester)
    ) {
      setToParticipant(selectedConversationRequester);
    }
  }, [
    getToParticipant,
    toIsModified,
    removeParticipant,
    selectedConversationRequester,
    selectedParticipants.items,
    selectedParticipants.to,
    setToParticipant,
  ]);

  const needToChangeCurrentRequester = useMemo(() => {
    return (
      !isInternalNote &&
      toIsModified &&
      needToChangeRequester(selectedParticipants.to, selectedConversationRequester)
    );
  }, [isInternalNote, selectedConversationRequester, selectedParticipants.to, toIsModified]);

  const customActionChips = useMemo(() => {
    if (needToChangeCurrentRequester) {
      return [getSetRequesterChip(selectedParticipants.to!)];
    }
    return [];
  }, [needToChangeCurrentRequester, selectedParticipants.to]);

  const pendingSeedRef = useRef<string | null>(null);

  const onMacroSelected = useCallback(
    (macro: Macro, interpolatedMessage: string) => {
      const nextIsNote =
        macro.messagePrivacy === 'private' ||
        (macro.messagePrivacy !== 'public' && isInternalNote);

      // validate + merge actions (chips)
      const updated = validateMacroActions([
        ...macroActions,
        ...macro.actions.map((a) => ({ ...a, id: crypto.randomUUID() })),
      ] as MacroActionRule[]);

      // build draft snapshot with meta.macros (editor doc remains clean)
      const snapshot = buildDraftJson(editorRef.current, updated, parsedDraft);
      lastAppliedRef.current = snapshot;

      // seed into the correct draft
      if (nextIsNote !== isInternalNote) {
        userChangedModeRef.current = true;
        pendingSeedRef.current = snapshot;
        setIsInternalNote(nextIsNote);
      } else {
        if (isViewingScheduledMessage) {
          debounceUpdateScheduledDraft(snapshot);
        } else {
          debounceUpdateBaseDraft(snapshot);
        }
      }

      const editor = editorRef.current;

      if (!editor || !editorHasContent(editor)) {
        editor?.commands.setContent(interpolatedMessage);
      } else {
        const endPos = editor.state.doc.content.size;
        editor.chain().setTextSelection(endPos).insertContent(interpolatedMessage).run();
      }

      setMacroActions(updated);
      showSuccessAlert('Macro applied');
    },
    [
      isInternalNote,
      isViewingScheduledMessage,
      macroActions,
      parsedDraft,
      debounceUpdateBaseDraft,
      debounceUpdateScheduledDraft,
      showSuccessAlert,
    ],
  );

  // after scope flips (because of a macro), seed new scope once via the hook
  useEffect(() => {
    if (!pendingSeedRef.current) return;
    if (isViewingScheduledMessage) {
      debounceUpdateScheduledDraft(pendingSeedRef.current);
    } else {
      debounceUpdateBaseDraft(pendingSeedRef.current);
    }
    pendingSeedRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInternalNote]);

  // delete exactly one chip (pure json meta update)
  const onActionDelete = useCallback(
    (index: number) => {
      const next = macroActions.filter((_, i) => i !== index);
      const snapshot = buildDraftJson(editorRef.current, next, parsedDraft);
      lastAppliedRef.current = snapshot;
      if (isViewingScheduledMessage) {
        debounceUpdateScheduledDraft(snapshot);
      } else {
        debounceUpdateBaseDraft(snapshot);
      }
      setMacroActions(next);
    },
    [
      isViewingScheduledMessage,
      macroActions,
      parsedDraft,
      debounceUpdateBaseDraft,
      debounceUpdateScheduledDraft,
    ],
  );

  const handleResetMacroActions = useCallback(() => {
    const editor = editorRef.current;
    const hasMacros = macroActions.length > 0;
    const hasContent = editorHasContent(editor);

    if (!hasMacros && !hasContent) return;

    editor?.commands?.clearContent?.(true);
    lastAppliedRef.current = '';
    setMacroActions([]);

    if (isViewingScheduledMessage) {
      void clearScheduledDraft();
    } else {
      void clearBaseDraft();
    }
  }, [clearBaseDraft, clearScheduledDraft, isViewingScheduledMessage, macroActions.length]);

  const canDeleteMacroActions = checkValidity([UserGroupRules.AbilityToRemoveMacroActions]);

  const { user } = useCurrentUserProfile();

  const onSendMessage = useCallback(
    async (body: string, mentions?: EntityIdentifier[]) => {
      const deferred = pendingSendDeferredRef.current;
      const actions = pendingSendActionsRef.current;
      pendingSendActionsRef.current = null;

      const resolveDeferred = (): void => {
        deferred?.resolve?.();
        if (pendingSendDeferredRef.current === deferred) {
          pendingSendDeferredRef.current = null;
        }
      };

      const rejectDeferred = (error: unknown): void => {
        deferred?.reject?.(error);
        if (pendingSendDeferredRef.current === deferred) {
          pendingSendDeferredRef.current = null;
        }
      };

      try {
        const normalizedBody = normalizeOutgoingMessageHtml(body);

        if (!user || (!normalizedBody?.trim() && !files?.length)) {
          resolveDeferred();
          return;
        }

        if (!isInternalNote && !hasEmailChannel(getToParticipant())) {
          showErrorAlert(
            "A public reply can't be sent because the requester has no email address. Add an email to their client record, then try again.",
          );
          setTimeout(() =>
            applyEditorContent(editorRef.current, body || activeRawDraft, lastAppliedRef),
          );
          resolveDeferred();
          return;
        }

        if (!conversation.subject || conversation.subject === NO_SUBJECT_PLACEHOLDER) {
          showErrorAlert('Conversation subject must not be empty');
          setTimeout(() =>
            applyEditorContent(editorRef.current, body || activeRawDraft, lastAppliedRef),
          );
          resolveDeferred();
          return;
        }

        const preparedParticipants = getPreparedParticipants({
          selectedParticipants,
          conversation,
          userId: user?.userId,
          userPartnerId: user?.partnerId,
        });

        if (needToChangeCurrentRequester && selectedParticipants.to) {
          setPartnerSelectedConversationRequester(
            getRequesterPlaceholder(selectedParticipants.to),
          );
        }

        const macrosFromDraft = parsedDraft ? readMacrosFromDoc(parsedDraft) : [];

        // this path is ONLY for "Send now" (not scheduling). For "Send later",
        // onSendMessage is NOT called — scheduling flow lives in useDraftScheduling.
        await onSubmit({
          message: {
            body: normalizedBody,
            isPrivate: isInternalNote,
            mentions,
            conversationMessageId,
          },
          macros: macrosFromDraft,
          participants: isInternalNote
            ? [
                getSenderParticipant(
                  user.userId,
                  user.partnerId !== conversation.partnerId ? user.partnerId : undefined,
                ),
              ]
            : preparedParticipants,
          activeChannels: isInternalNote ? undefined : getActiveChannels(preparedParticipants),
          sendAndActions: actions ?? undefined,
        });

        if (actions?.length) {
          notifySendAndActions(actions);
        }

        if (baseDraftSchedule?.sideEffects?.actions?.length) {
          await updateBaseDraftSchedule({ sideEffects: null }, { allowEmpty: true });
        }

        // Immediate send: clear the active draft instance and the editor UI.
        // (Scheduling does NOT come through here, so we do NOT touch scheduled drafts.)
        if (isViewingScheduledMessage) {
          await clearScheduledDraft();
        } else {
          await clearBaseDraft();
        }

        // clear editor immediately to avoid lingering content while store updates
        editorRef.current?.commands?.clearContent?.(true);
        lastAppliedRef.current = '';
        setMacroActions([]);
        resetAiAssistant();
        resolveDeferred();
      } catch (error) {
        rejectDeferred(error);
        throw error;
      }
    },
    [
      notifySendAndActions,
      baseDraftSchedule?.sideEffects?.actions?.length,
      activeRawDraft,
      user,
      files?.length,
      isInternalNote,
      getToParticipant,
      selectedParticipants,
      conversation,
      needToChangeCurrentRequester,
      onSubmit,
      conversationMessageId,
      resetAiAssistant,
      clearBaseDraft,
      clearScheduledDraft,
      showErrorAlert,
      setPartnerSelectedConversationRequester,
      parsedDraft,
      updateBaseDraftSchedule,
      isViewingScheduledMessage,
    ],
  );

  const handleMessageTypeChange = useCallback(
    (val: ConversationChannelTypeExtended) => {
      const nextIsNote = val === ConversationChannelTypeExtended.InternalNote;
      const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
      lastAppliedRef.current = snapshot;

      if (isViewingScheduledMessage) {
        flushUpdateScheduledDraft(snapshot);
      } else {
        flushUpdateBaseDraft(snapshot);
      }

      if (nextIsNote !== isInternalNote) {
        userChangedModeRef.current = true;
        setIsInternalNote(nextIsNote);
      }
    },
    [
      isViewingScheduledMessage,
      macroActions,
      parsedDraft,
      flushUpdateBaseDraft,
      flushUpdateScheduledDraft,
      isInternalNote,
    ],
  );

  const { showModal: showSnoozeModal } = useModalMethods(Modals.SnoozeConversationModal);
  const { showModal: showMacrosModal } = useModalMethods(Modals.MacrosModal);
  useRegisterShortcuts(
    useMemo(
      () => ({
        ...appShortcuts.openSnooze,
        callback: () => {
          showSnoozeModal();
          return false;
        },
      }),
      [showSnoozeModal],
    ),
  );

  const openMacrosModal = useCallback(() => {
    showMacrosModal({
      conversation,
      onMacroSelected,
    });
  }, [conversation, onMacroSelected, showMacrosModal]);

  useRegisterQuickActions(
    useMemo(
      () => [
        {
          id: 'conversation-open-macros',
          title: 'Macros',
          description: 'Browse and apply macros',
          icon: <DynamicIcon name="bolt" />,
          group: 'Conversation Actions',
          handler: openMacrosModal,
          order: 170,
          keywords: ['macros', 'templates', 'replies'],
        },
      ],
      [openMacrosModal],
    ),
  );

  const handleGetMentionPopup = useCallback(
    (
      mentionUpdatePopupStateSubject: Subject<Partial<MentionsPopupState>>,
      onMentionKeyDownRef: MutableRefObject<((event: KeyboardEvent) => boolean) | null>,
    ) => (
      <MentionPopup
        onMentionKeyDownRef={onMentionKeyDownRef}
        updatePopupStateSubject={mentionUpdatePopupStateSubject}
        partnerId={conversation.partnerId}
      />
    ),
    [conversation.partnerId],
  );

  // persist on hide/unload – write to the active draft
  const flushDraftNow = useCallback(() => {
    const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
    if (snapshot !== lastAppliedRef.current) {
      lastAppliedRef.current = snapshot;
    }
    if (isViewingScheduledMessage) {
      flushUpdateScheduledDraft(snapshot);
    } else {
      flushUpdateBaseDraft(snapshot);
    }
  }, [
    isViewingScheduledMessage,
    macroActions,
    parsedDraft,
    flushUpdateBaseDraft,
    flushUpdateScheduledDraft,
  ]);

  const [isMessageEmpty, setIsMessageEmpty] = useState(true);
  const [isScheduleConflictOpen, setIsScheduleConflictOpen] = useState(false);
  const [isReplaceMode, setIsReplaceMode] = useState(false);

  useEffect(() => {
    setIsMessageEmpty(computeIsMessageEmpty());

    const id = window.setInterval(() => {
      setIsMessageEmpty(computeIsMessageEmpty());
    }, 250);

    return () => window.clearInterval(id);
  }, [computeIsMessageEmpty]);

  // flush before toggling view to avoid losing edits
  const handleViewScheduledMessageSafe = useCallback(() => {
    const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
    lastAppliedRef.current = snapshot;
    flushUpdateBaseDraft(snapshot); // save base before switching
    handleViewScheduledMessage();
  }, [macroActions, parsedDraft, flushUpdateBaseDraft, handleViewScheduledMessage]);

  const handleDoneViewingScheduledMessageSafe = useCallback(() => {
    const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
    lastAppliedRef.current = snapshot;
    flushUpdateScheduledDraft(snapshot); // save scheduled before switching back
    handleDoneViewingScheduledMessage();
  }, [
    macroActions,
    parsedDraft,
    flushUpdateScheduledDraft,
    handleDoneViewingScheduledMessage,
  ]);

  const handleSendOptionSelectGuarded = useCallback(
    (optionId: string): Promise<void> | void => {
      if (hasScheduledMessage && !isViewingScheduledMessage) {
        handleViewScheduledMessageSafe();
      }
      return handleSendOptionSelect(optionId);
    },
    [
      handleSendOptionSelect,
      handleViewScheduledMessageSafe,
      hasScheduledMessage,
      isViewingScheduledMessage,
    ],
  );

  const renderPrimaryAction = useCallback(
    ({
      isLoading,
      disabled,
      buttonText,
      buttonRef,
      onClick,
      startIcon,
    }: {
      isLoading: boolean;
      disabled?: boolean;
      buttonText?: string;
      buttonRef: MutableRefObject<HTMLButtonElement | null>;
      onClick: () => void;
      startIcon?: string;
    }) => {
      rteSubmitButtonRef.current = buttonRef;
      sendNowHandlerRef.current = onClick;

      const shouldDisable = Boolean(
        disabled || isApplyingSendOption || isMessageEmpty || isViewingScheduledMessage,
      );

      const onOpenOptionsGuard = () => {
        if (hasScheduledMessage && !isViewingScheduledMessage) {
          setIsReplaceMode(false);
          setIsScheduleConflictOpen(true);
          return;
        }
        openScheduleModal();
      };

      return (
        <SendOptionsButton
          buttonText={buttonText}
          buttonRef={buttonRef}
          isLoading={isLoading || isApplyingSendOption}
          disabled={shouldDisable}
          onSend={onClick}
          startIcon={startIcon}
          onOpenOptions={onOpenOptionsGuard}
        />
      );
    },
    // eslint-disable-next-line
    [
      handleViewScheduledMessageSafe,
      hasScheduledMessage,
      isApplyingSendOption,
      isMessageEmpty,
      isViewingScheduledMessage,
      openScheduleModal,
    ],
  );

  useEffect(() => {
    window.addEventListener('online', connect);
    window.addEventListener('offline', disconnect);

    function connect() {
      setIsOffline(false);
    }

    function disconnect() {
      setIsOffline(true);
    }

    return () => {
      window.removeEventListener('online', connect);
      window.removeEventListener('offline', disconnect);
    };
  }, []);

  useDraftCommitGuards(flushDraftNow);

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <PendingAiSuggestionHeader
        promptName={selectedPromptName}
        show={!!pendingAiSuggestion}
        onDismiss={handleDismissAiSuggestion}
        onRefine={handleRefineSuggestion}
        onAccept={handleAcceptAiSuggestion}
      />
      <InputFieldBackground
        isInternalNote={isInternalNote}
        hasPendingAiSuggestion={!!pendingAiSuggestion}
      >
        {isGeneratingAiSuggestion ? <SuggestionGenerationLoader /> : null}

        {hasSendableScheduledMessage || isViewingScheduledMessage || isScheduleDiscarded ? (
          <Stack spacing={1} mb={1}>
            <ScheduledBanner
              scheduledLabel={scheduledLabel}
              onView={handleViewScheduledMessageSafe}
              onReschedule={handleReschedule}
              onDiscard={handleDiscardScheduledMessage}
              isSaving={isScheduledDraftSaving}
              conditions={scheduledConditions}
              isEditing={isViewingScheduledMessage}
              onDoneEditing={handleDoneViewingScheduledMessageSafe}
              status={isScheduleDiscarded ? 'discarded' : 'scheduled'}
              discardedLabel={scheduledDiscardedLabel}
              attachmentCount={scheduledServerAttachments.length}
            />
          </Stack>
        ) : null}

        <MessageTypeSelectorBackground isMobile={isMobile}>
          <MessageTypeSelector
            options={messageTypeOptions}
            selectedValue={
              isInternalNote
                ? ConversationChannelTypeExtended.InternalNote
                : ConversationChannelTypeExtended.Chat
            }
            onMessageTypeChange={handleMessageTypeChange}
          />
          {!isInternalNote && (
            <ParticipationPanel
              conversation={conversation}
              selectedParticipants={selectedParticipants}
              toIsModified={toIsModified}
              getToParticipant={getToParticipant}
              removeParticipant={removeParticipant}
              setToParticipant={setToParticipant}
              {...selectedParticipantsCallbacks}
            />
          )}
        </MessageTypeSelectorBackground>

        <Suspense>
          <RichTextEditor
            disabled={isOffline}
            fieldAutoSize={fieldAutoSize}
            fieldMinHeight={fieldMinHeight}
            content={parsedDraft ? stripMeta(parsedDraft) : activeRawDraft}
            onSubmit={onSendMessage}
            editorRef={editorRef}
            editorOptions={editorOptions}
            onBlur={handleEditorBlur}
            placeholderOptions={placeholderOptions}
            toolbarOptions={[
              <MacrosTrigger
                key="macros-trigger"
                onMacroSelected={onMacroSelected}
                conversation={conversation}
              />,
              <AiAssistantTrigger
                key="ai-assistant-trigger"
                onSubmit={(promptId?: string, promptName?: string, customPrompt?: string) =>
                  getInitialSuggestion({
                    promptId,
                    promptName,
                    customPrompt,
                    userId: user?.userId,
                    userInput: editorRef.current?.getHTML() || '',
                  })
                }
              />,
            ]}
            buttonText={isInternalNote ? 'Add Note' : 'Send Message'}
            secondaryButtonText="Snooze"
            secondaryButtonTooltipText={
              <ShortcutText
                label="Snooze"
                shortcut={appShortcuts.openSnooze.command}
                size="small"
              />
            }
            secondaryButtonIcon="snooze"
            handleSecondaryButton={showSnoozeModal}
            watchContent
            files={isViewingScheduledMessage ? scheduledFiles : files}
            setFiles={isViewingScheduledMessage ? setScheduledFiles : setFiles}
            slots={{
              contentFooter: (
                <AppliedMacroActionsList
                  onActionDelete={canDeleteMacroActions ? onActionDelete : undefined}
                  onReset={canDeleteMacroActions ? handleResetMacroActions : undefined}
                  actions={macroActions}
                  customChips={customActionChips}
                  partnerId={conversation.partnerId}
                />
              ),
            }}
            onGetMentionPopup={handleGetMentionPopup}
            renderPrimaryAction={renderPrimaryAction}
          />
        </Suspense>
      </InputFieldBackground>
      <ExistingScheduledConflictModal
        open={isScheduleConflictOpen}
        scheduledLabel={scheduledLabel}
        previewText={buildPreviewFromJson(scheduledDraftContentRef.current || '')}
        onCancel={() => setIsScheduleConflictOpen(false)}
        onEditExisting={() => {
          setIsScheduleConflictOpen(false);
          setIsReplaceMode(false);
          handleViewScheduledMessageSafe();
          openScheduleModal();
        }}
        onReplaceWithNew={() => {
          setIsScheduleConflictOpen(false);
          setIsReplaceMode(true);
          openScheduleModal();
        }}
      />
      <ScheduleSendModal
        open={isScheduleModalOpen}
        initialDate={isReplaceMode ? null : (scheduledDraftSchedule?.scheduledAt ?? null)}
        initialConditions={scheduledConditions}
        sendOptions={sendOptions}
        onClose={() => {
          setIsReplaceMode(false);
          closeScheduleModal();
        }}
        onSubmit={(p) => {
          const res = handleScheduleModalSubmit({
            ...p,
            mode: isReplaceMode ? 'replace' : 'edit',
          });
          setIsReplaceMode(false);
          return res;
        }}
        onSelectOption={handleSendOptionSelectGuarded}
        isProcessingOption={isApplyingSendOption}
      />
    </Box>
  );
};

function needToChangeRequester(
  to: SelectedParticipants['to'],
  selectedRequester: PreparedConversationParticipantOption | null,
): boolean {
  if (!to) return false;
  if (!selectedRequester) return true;
  return (
    to.participantEntity !== selectedRequester.participantEntity ||
    to.participantIdentity !== selectedRequester.id
  );
}

function getSetRequesterChip(toRecipient: Required<SelectedParticipants>['to']): CustomChip {
  const name = toRecipient.name || toRecipient.participantIdentity;
  return {
    id: 'set-requester',
    icon: <ActionChipIcon name="person" />,
    label: name,
    tooltip: `Update requester to ${name}`,
  };
}

function getRequesterPlaceholder(
  toParticipant: SelectedParticipants['to'],
): PreparedConversationParticipantOption {
  return {
    id: toParticipant!.participantIdentity,
    value: '',
    label: toParticipant!.name || toParticipant!.participantIdentity,
    group: '',
    participantEntity: toParticipant!.participantEntity,
    conversationParticipantId: '',
    phoneNumber: '',
    email: '',
    avatarFileKey: null,
  };
}

function resolveStatusAction(
  value: string | null | undefined,
): { status: ConversationStatus; label: string } | null {
  if (!value) return null;

  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;

  const mapping: Record<string, { status: ConversationStatus; label: string }> = {
    solved: { status: ConversationStatus.Solved, label: 'conversation solved' },
    open: { status: ConversationStatus.Open, label: 'conversation reopened' },
    closed: { status: ConversationStatus.Closed, label: 'conversation closed' },
    new: { status: ConversationStatus.New, label: 'conversation marked as new' },
    on_hold: { status: ConversationStatus.OnHold, label: 'conversation placed on hold' },
  };

  return mapping[normalized] ?? null;
}

/** Hydrate: json-first (strip meta), fallback HTML, dedupe by comparing current editor content. */
function applyEditorContent(
  ed: Editor | null,
  raw: string,
  lastApplied: React.MutableRefObject<string>,
) {
  if (!ed || !raw) return;
  if (raw === lastApplied.current) return;

  try {
    const parsed = JSON.parse(raw) as EditorDoc;
    const target = stripMeta(parsed);
    const curJSON = JSON.stringify(ed.getJSON());
    const nextJSON = JSON.stringify(target);
    if (curJSON !== nextJSON) {
      ed.commands.setContent(target);
    }
    lastApplied.current = nextJSON;
    return;
  } catch {
    // not json - treat as HTML
  }

  const curHtml = (ed.getHTML() || '').trim();
  const nextHtml = (raw || '').trim();
  if (curHtml !== nextHtml) {
    ed.commands.setContent(nextHtml);
  }
  lastApplied.current = nextHtml;
}

/** Remove meta from a draft JSON before giving it to the editor. */
function stripMeta(doc: JSONContent) {
  const { meta, ...rest } = doc || {};
  return rest as string & Content;
}

/** Build the stringified draft json: current editor doc + macros in meta (editor doc stays clean). */
function buildDraftJson(
  editor: Editor | null,
  macros: (MacroActionRule & { isValid: boolean })[],
  prev: EditorDoc | null,
): string {
  const cleanDoc = editor?.getJSON?.() ?? {};
  const prevMeta = prev?.meta ?? {};
  const merged = { ...cleanDoc, meta: { ...prevMeta, macros } };
  return stableStringify(merged);
}

function buildPreviewFromJson(raw: string, lineLimit = 3): string {
  if (!raw) return '';
  try {
    const html = renderRichTextDocumentToHtml(raw);
    const text = (htmlToPlainText(html) || '').trim();
    if (!text) return '';
    const lines = text.split(/\s*\n+\s*|(?<=\.)\s+/).filter(Boolean); // split by newlines or sentence breaks
    const limited = lines.slice(0, lineLimit).join('\n');
    return limited.length > 500 ? `${limited.slice(0, 500)}…` : limited;
  } catch {
    const text = (htmlToPlainText(raw) || '').trim();
    return text.length > 500 ? `${text.slice(0, 500)}…` : text;
  }
}

function editorHasContent(ed: Editor | null): boolean {
  const text = ed?.getText?.() ?? '';
  return text.trim().length > 0;
}
